import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Input } from "./ui/input";
import { Loader2, Search, Building2 } from "lucide-react";

/**
 * ClienteAutocomplete — busca cliente em /api/admin/clientes (locativo + crm).
 *
 * Props:
 *   value         — string (nome exibido no input)
 *   onChange(str) — controla o texto digitado
 *   onSelect(cli) — callback ao escolher um item: { id, nome, cnpj_cpf, tipo_cliente, origem, ... }
 *   placeholder   — placeholder do input
 *   disabled
 *   minChars      — caracteres mínimos para disparar busca (default 2)
 */
export default function ClienteAutocomplete({
  value = "",
  onChange,
  onSelect,
  placeholder = "Buscar cliente por nome ou CNPJ...",
  disabled = false,
  minChars = 2,
  testId = "cliente-autocomplete",
}) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Fecha ao clicar fora
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value || value.trim().length < minChars) {
      setResults([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get("/clientes-busca", {
          params: { q: value.trim(), limit: 12 },
        });
        setResults(r.data?.items || []);
        setOpen(true);
        setHighlight(0);
      } catch (err) {
        setResults([]);
        setOpen(true); // mantém dropdown aberto pra mostrar "Nenhum cliente encontrado"
      } finally {
        setLoading(false);
      }
    }, 280);
    return () => clearTimeout(debounceRef.current);
  }, [value, minChars]);

  const escolher = (c) => {
    setOpen(false);
    onSelect?.(c);
  };

  const onKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      escolher(results[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={wrapperRef} data-testid={testId}>
      <div className="relative">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className="pl-8"
          data-testid={`${testId}-input`}
        />
        {loading && (
          <Loader2 size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />
        )}
      </div>

      {open && (
        <div className="absolute z-50 mt-1 left-0 right-0 max-h-72 overflow-y-auto border border-slate-200 rounded-md bg-white shadow-lg"
             data-testid={`${testId}-dropdown`}>
          {results.length === 0 && !loading && (
            <div className="px-3 py-4 text-xs text-slate-400 text-center">
              {value.trim().length < minChars
                ? `Digite ao menos ${minChars} caracteres`
                : `Nenhum cliente encontrado para "${value}"`}
            </div>
          )}
          {results.map((c, idx) => (
            <button
              key={c.id}
              type="button"
              onClick={() => escolher(c)}
              onMouseEnter={() => setHighlight(idx)}
              className={`w-full text-left px-3 py-2 border-b border-slate-100 last:border-b-0 text-xs flex items-start gap-2 ${
                idx === highlight ? "bg-orange-50" : "hover:bg-slate-50"
              }`}
              data-testid={`${testId}-item-${idx}`}
            >
              <Building2 size={12} className="text-slate-400 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-800 truncate">{c.nome}</div>
                <div className="text-[10px] text-slate-500 flex gap-2 mt-0.5">
                  {c.cnpj_cpf && <span className="font-mono">{c.cnpj_cpf}</span>}
                  {c.tipo_cliente && <span className="uppercase text-violet-600">{c.tipo_cliente}</span>}
                  <span className="text-slate-400">{c.origem === "locativo" ? "Base" : "CRM"}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
